/* eslint dot-notation: "off" */
import {
  put, select, call, all, cancelled,
} from 'redux-saga/effects';
import _ from 'lodash';
import bboxPolygon from '@turf/bbox-polygon';
import {polygon} from '@turf/helpers';
import {getCoords} from '@turf/invariant';
import UriJs from 'urijs';
import {cacheAdapterEnhancer, throttleAdapterEnhancer} from 'axios-extensions';
import axios, {CancelToken} from 'axios';

import {gql} from '@apollo/client';
import apolloClient from '../apolloClient';
import {getQueryParam, setQueryParamArray} from '../../helpers/common';
import * as actions from '../actions';
import utilities from '../../utilities/utilities';
import {prepareActionsFields, getCustomValue} from './helpers/common';
import {prepareOverlayGeometry} from '../../Components/Containers/Map/Elements/helpers/common';
import {hasAdditionalFieldAccess, propertyResultsLimit} from '../../helpers/subscriptionCheck';
import {BASE_URL_TYPE} from '../../api/axios';
import {getActualPublicTokenGenerator} from '../../utilities/utilitiesToken';
import FiltersParser, {urlHasRestorableFilterParams} from '../../helpers/urlParsers/FiltersParser';

const API_URL = BASE_URL_TYPE.API;
const RESULTS_TAB_BOOST_QUERY = JSON.stringify([
  {
    field: 'exclusionStatus',
    value: '*',
    negate: true,
    boost: 2,
  },
  {field: 'address', value: '*', boost: 1},
]);

let clustersCancel = null;

// Create `axios` instance passing the newly created `cache.adapter`
const api = axios.create({
  adapter: throttleAdapterEnhancer(cacheAdapterEnhancer(axios.getAdapter(axios.defaults.adapter))),
});

const prevData = {
  clustersUrl: '',
  shapesUrl: '',
};

const facetFilters = _.memoize(filters =>
  _.map(
    _.filter(
      _.flatMapDeep(filters, group => _.flatMapDeep(group.subgroups, subgroup => subgroup.filters)),
      filter => _.get(filter, 'settings.type') === 'facet' && _.isEmpty(filter.jurisdiction_filter),
    ),
    'name',
  ));

const extendWithAdditionalData = (item, addtionalItem, actionData) => {
  _.each(actionData.fields, (field, fieldKey) => {
    const fieldSettings = _.get(actionData, ['fieldsSettings', fieldKey], {});
    if (fieldSettings.field_type === 'custom') {
      const {output} = getCustomValue(fieldSettings.custom_value, addtionalItem);
      _.defaults(item, {[fieldKey]: output});
    }
    else {
      _.defaults(item, {[fieldKey]: _.get(addtionalItem, field, '')});
    }
  });
};

/**
 * Get user public API Token.
 */
function* apiToken() {
  return yield getActualPublicTokenGenerator();
}

/**
 * Add notes request
 * @param {*} extraActions
 *   Extra actions object.
 * @param {*} extraRequests
 *   Extra request array.
 * @param {*} ids
 *   List of ids.
 */
function* addNotesRequest(extraActions, extraRequests, ids) {
  const userData = yield select(state => state.user.userData);
  if (utilities.isLoggedIn(userData)) {
    extraActions['notes'] = {fields: {notes_count: 'notes_count'}};
    extraRequests.push(
      call(axios, {
        url: `${BASE_URL_TYPE.PROPZONE}/api/v1/property_notes/list?id=${ids.join()}`,
        method: 'get',
        withCredentials: true,
        headers: {
          'X-CSRF-Token': userData.token,
        },
      }),
    );
  }
}

/**
 * Saved properties request
 * @param {*} fields
 *   List of fields.
 * @param {*} ids
 *   List of ids.
 * @param {*} pager
 *   Pager options. Example {limit: 10, offset: 0}.
 */
function* SavedPropertiesRequest(fields, ids = [], pager = {}) {
  const userData = yield select(state => state.user.userData);
  if (utilities.isLoggedIn(userData)) {
    const variables = {
      uid: userData.current_uid,
    };
    const params = ['$uid: [String]'];
    const conditions = ['{field: "user_id", value: $uid}', '{field: "field_list_entity", operator: IS_NOT_NULL}'];
    if (ids && ids.length) {
      variables.ids = ids;
      params.push('$ids: [String!]!');
      conditions.push('{field: "property_id", value: $ids, operator: IN}');
    }
    const followGql = gql`query(${params.join(', ')})
    {
      entityQuery(
        entityType: SEAR_PROPERTY
        limit: ${pager.limit || 20}
        offset: ${pager.offset || 0}
        filter: {conditions: [${conditions.join(', ')}]}) {
        items {
          ... on SearProperty {
            ${fields.join('\r\n')}
          }
        }
        total
      }
    }
  `;
    return call(apolloClient.query, {
      query: followGql,
      variables,
      fetchPolicy: 'network-only',
    });
  }
}

/**
 * Prepare search params.
 * @param {object} formData
 *   Form data object.
 * @param {boolean} asObject
 *   If TRUE returns params as object.
 */
export function prepareFormDataParams(formData, asObject = false) {
  const params = {};
  _.each(formData, (data, key) => {
    let paramValue = null;
    let paramName = data.paramName || key;
    switch (data.type) {
      case 'autocomplete':
      case 'facet':
        paramValue = encodeURIComponent(Object.keys(data.value).join(','));
        if (key === 'folio' && !paramValue.includes('property_record')) {
          paramName = 'folio';
        }
        break;
      case 'list_filter':
        if (typeof data.value === 'object') {
          paramValue = Object.keys(data.value).join(',');
        }
        else {
          paramValue = data.value;
        }
        break;
      case 'date_filter': {
        const fromDate = data.value.from;
        const toDate = data.value.to;
        if (fromDate) {
          params[`${paramName}_from`] = fromDate;
        }
        if (toDate) {
          params[`${paramName}_to`] = toDate;
        }
        break;
      }
      case 'range_filter': {
        const min = data.value.min || '*';
        const max = data.value.max || '*';
        paramValue = `${min}-${max}`;
        break;
      }
      case 'proximity': {
        const values = _.cloneDeep(data.value);
        values.lat = [];
        values.lon = [];
        values.dist = [];
        values.reverse = [];
        const items = _.get(values, 'items', {});
        _.each(items, (item) => {
          // Put distance in meters.
          values.dist.push(item.distance * 1609.344);
          values.reverse.push(item.reverse);
          values.lat.push(item.latlon.lat);
          values.lon.push(item.latlon.lon);
        });
        delete values.items;
        if (params[paramName]) {
          const prevValues = JSON.parse(params[paramName]);
          _.each(['lat', 'lon', 'dist', 'reverse'], (item) => {
            prevValues[item] = prevValues[item].concat(values[item]);
          });
          paramValue = JSON.stringify(prevValues);
        }
        else {
          paramValue = JSON.stringify(values);
        }
        break;
      }
      case 'search_polygon':
        paramValue = encodeURIComponent(JSON.stringify(data.value));
        break;
      default:
        paramValue = data.value;
    }
    if (paramValue) {
      params[paramName] = paramValue;
    }
  });
  if (asObject) {
    return params;
  }
  let paramsString = '';
  _.each(params, (value, key) => {
    paramsString += `&${key}=${value}`;
  });
  return paramsString;
}

/**
 * Update facets info.
 */
export function* updateFacets(action, setLoading = true) {
  if (setLoading) {
    yield put(actions.setLoading(true));
  }
  const singleFacet = _.get(action, 'data.single');
  const clearResults = _.get(action, 'data.clear');
  const {filters} = action.data;
  let facetsUrl = '';

  const {formData} = action.data;

  try {
    let results = {};
    if (!clearResults) {
      const mapFormData = yield select(state => state.map.mapFormData);
      const placeId = yield select(state => state.map.navigation.place?.placeId);
      if (!placeId) {
        return;
      }

      const mapData = yield select(state => state.map.mapData);
      if (!mapData.data_source) {
        return;
      }
      const apiAction = !singleFacet && _.get(filters, 'action') ? filters.action : mapFormData.filters.action;
      let facetsList = [];
      if (singleFacet) {
        facetsList = [filters.name];
      }
      else {
        facetsList = facetFilters(_.get(filters, 'groups', []));
      }
      if (!facetsList.length) {
        return;
      }
      // Add selected filters to each request url.
      const filterParams = prepareFormDataParams(formData, true);
      const stateEnv = utilities.getCurrentStateEnv();
      const apiPublicToken = yield apiToken();
      facetsUrl = new UriJs(`${API_URL}/api/ui-api`).search({
        rows: 100,
        offset: 0,
        type: 'facets_info',
        action: apiAction,
        featureLayer: mapData.data_source,
        publicToken: apiPublicToken,
        'fields[]': facetsList,
        group_by: 'condoGroup',
        allFacets: '1',
        state_env: stateEnv,
        place: placeId,
      });

      facetsUrl = facetsUrl
        .addSearch(filterParams)
        .normalizeSearch()
        .readable();

      results = yield call(api, facetsUrl);
      if (singleFacet && !_.get(results, ['data', 'facets', filters.name])) {
        results = {data: {facets: {[filters.name]: []}}};
      }
    }
    else {
      results = {data: {facets: {[filters.name]: []}}};
    }

    const {facets} = results.data;
    const newFormData = {...formData};
    Object.entries(facets).forEach(([name, values]) => {
      const data = newFormData[name];
      if (data) {
        values.forEach((value) => {
          if (data.value[value.filter]) {
            newFormData[name].value[value.filter] = value.title;
          }
        });
      }
    });

    yield put(
      actions.setMapFormData(prevFormData => ({
        formData: {
          ...prevFormData.formData,
          ...newFormData,
        },
      })),
    );

    yield put(
      actions.updateMapSearchResults(prevDataResults => ({
        filters: {
          ...prevDataResults.filters,
          facets: {...prevDataResults.filters.facets, ...results.data.facets},
          count: !singleFacet ? results.data.count : prevDataResults.filters.count,
        },
      })),
    );
    if (setLoading) {
      yield put(actions.setLoading(false));
    }
  }
  catch (e) {
    const yieldActions = [put(actions.updateMapSearchResults({filters: {}}))];
    if (setLoading) {
      yieldActions.push(put(actions.setLoading(false)));
    }
    yield all(yieldActions);
  }
}

/**
 * Update clusters sagas handler for updating clusters and shapes
 * depending on zoom.
 */
export function* updateClusters(action, init = false) {
  const mapSearchResults = yield select(state => state.map.mapSearchResults);
  const searchInited = init || mapSearchResults.searchInited || mapSearchResults.clustersInited;
  const passiveAction = !!_.get(action, 'mapGeometry') && !_.get(action, 'mapGeometry.boundBox', false);
  // If search not inited or action was runned just for adding data to
  // redux store(like default bounds of prev zoom center).
  if (!searchInited || passiveAction) {
    return;
  }

  const mapData = yield select(state => state.map.mapData);
  if (!mapData.data_source) {
    return;
  }
  const mapFormData = yield select(state => state.map.mapFormData);
  const mapGeometry = yield select(state => state.map.mapGeometry);
  const {boundBox, zoom} = mapGeometry;
  const {formData, filters} = mapFormData;
  const apiPublicToken = yield apiToken();

  // Add selected filters to each request url.
  const filterParams = prepareFormDataParams(formData, true);
  const stateEnv = utilities.getCurrentStateEnv();
  const placeId = yield select(state => state.map.navigation.place?.placeId);
  const gridInitialized = yield select(state => state.map.gridInitialized);

  // Prepare requests urls.
  const hasParams = !_.isEmpty(filterParams);
  let clustersUrl = '';
  let shapesUrl = '';
  let skipShapes = false;
  if (hasParams && placeId && gridInitialized) {
    clustersUrl = new UriJs(`${API_URL}/fast-ajax/map_clusters`).search({
      map: 'property/map',
      publicToken: apiPublicToken,
      featureLayer: mapData.data_source,
      state_env: stateEnv,
      group_by: 'condoGroup',
    });
    clustersUrl = clustersUrl.addSearch('place', placeId);
    clustersUrl = clustersUrl
      .addSearch(filterParams)
      .normalizeSearch()
      .readable();

    shapesUrl = new UriJs(`${API_URL}/api/ui-api`).search({
      rows: 200,
      offset: 0,
      type: filters.action,
      action: filters.action,
      publicToken: apiPublicToken,
      featureLayer: mapData.data_source,
      geometryFormat: 'geojson',
      'fields[]': ['id'],
      group_by: 'condoGroup',
      state_env: stateEnv,
      'subquery[0][action]': '_property_record',
      'subquery[0][fields][]': ['_tags:redevelopmentScore', 'id'],
      'subquery[0][from_field]': 'id',
      'subquery[0][to_field]': 'groupId',
    });
    if (placeId) {
      shapesUrl = shapesUrl.addSearch('place', placeId);
    }
    shapesUrl = shapesUrl.addSearch(filterParams);

    if (boundBox) {
      clustersUrl += `&bbox=${boundBox.toArray().toString()}`;
    }
    if (zoom) {
      const modZoom = zoom > 16 ? Math.floor(zoom) : Math.ceil(zoom);
      clustersUrl += `&zoom=${modZoom}`;
      if (zoom >= 17) {
        let boundBoxPolygon = bboxPolygon(_.flatten(boundBox.toArray()));
        if (formData.search_polygon) {
          // Loaded on demand: @turf/intersect pulls in polygon-clipping (~71 KB
          // gzip). It is only needed for in-polygon search at high zoom, so
          // keep it out of the eager bundle that gates first paint.
          const {default: intersect} = yield call(() => import('@turf/intersect'));
          const searchPolygon = polygon(formData.search_polygon.value[0]);
          const polygonsIntersection = intersect(searchPolygon, boundBoxPolygon);
          if (polygonsIntersection) {
            boundBoxPolygon = polygonsIntersection;
          }
          else {
            skipShapes = true;
          }
        }
        if (shapesUrl.hasSearch('search_polygon')) {
          shapesUrl.removeSearch('search_polygon');
        }
        shapesUrl.addSearch('search_polygon', encodeURIComponent(JSON.stringify(getCoords(boundBoxPolygon))));
      }
    }
    shapesUrl = shapesUrl.normalizeSearch().readable();
  }
  if (clustersCancel) {
    yield call(clustersCancel, 'Cancel previous clusters call');
  }
  try {
    const getParamsUrl = url => (hasParams ? url : `${url}&params=no-params`);
    let shapesData = {};
    // Depends on zoom prepare shapes or clusters(circles) request.
    if (zoom && zoom >= 17 && !skipShapes) {
      const deferShapes = hasParams && placeId && !gridInitialized && !shapesUrl;
      if (!deferShapes && getParamsUrl(shapesUrl) !== prevData.shapesUrl) {
        if (shapesUrl && hasParams) {
          shapesData = (yield call(api, shapesUrl, {
            cancelToken: new CancelToken((c) => {
              clustersCancel = c;
            }),
          })).data;
        }
        yield all([put(actions.updateMapSearchResults({shapes: shapesData}))]);
      }
    }
    if (zoom && zoom < 17) {
      // On remount (e.g. map → CRM → map), geometry/updateClusters often runs before
      // LayersSearchGrid sets gridInitialized. clustersUrl is then empty but this branch
      // would still dispatch empty clusters and clear the map until the next resize/moveend.
      const deferClusters = hasParams && placeId && !gridInitialized && !clustersUrl;
      if (!deferClusters && getParamsUrl(clustersUrl) !== prevData.clustersUrl) {
        let clustersData = {};
        if (clustersUrl) {
          clustersData = (yield call(axios, clustersUrl, {
            cancelToken: new CancelToken((c) => {
              clustersCancel = c;
            }),
          })).data;
        }
        /**
         * Need to fetch current state status (Do we still need the result)
         */
        const updatedSearchResults = yield select(state => state.map.mapSearchResults);
        const updatedSearchInited = init || updatedSearchResults.searchInited || updatedSearchResults.clustersInited;
        if (updatedSearchInited) {
          yield all([
            put(
              actions.updateMapSearchResults({
                clusters: hasParams ? clustersData : {overlay: clustersData.overlay},
                shapes: hasParams ? shapesData : {},
              }),
            ),
          ]);
        }
      }
    }
    if (mapSearchResults.clutersLoading) {
      yield put(actions.updateMapSearchResults({clutersLoading: false}));
    }
    prevData.clustersUrl = getParamsUrl(clustersUrl);
    prevData.shapesUrl = getParamsUrl(shapesUrl);
  }
  catch (e) {
    yield all([put(actions.updateMapSearchResults({}))]);
  }
  finally {
    if (yield cancelled()) {
      yield call(clustersCancel, 'Cancel previous clusters call');
    }
  }
}

/**
 * After search grid layers attach to the map, rerun cluster/shape fetch if a search is active.
 * Otherwise the first post-remount updateClusters can no-op (empty cluster/shape URLs) and never
 * run again until map geometry changes (e.g. resize).
 */
export function* refreshClustersAfterGridInit(action) {
  if (!action.data) {
    return;
  }
  const mapSearchResults = yield select(state => state.map.mapSearchResults);
  if (!mapSearchResults.searchInited) {
    return;
  }
  prevData.clustersUrl = '';
  prevData.shapesUrl = '';
  yield call(updateClusters, {}, true);
}

/**
 * Help function for feetching follows and notes.
 * @param {*} ids
 *   Property ids.
 * @param {*} items
 *   Properties list.
 * @param {*} extraActions
 *   Extra actions list.
 * @param {*} extraRequests
 *   Extra request list.
 */
function* additionalRequests(ids, items, extraActions, extraRequests) {
  // Add notes status request.
  yield addNotesRequest(extraActions, extraRequests, ids);
  extraActions['_follow'] = {fields: {followId: 'followId', groupId: 'groupId'}};
  extraRequests.push(yield SavedPropertiesRequest(['id', 'propertyId'], ids));

  // Request additional data.
  const additionalData = yield all(extraRequests);
  // Prepare additional data merging with main items.
  const extraActionsKeys = Object.keys(extraActions);
  _.each(items, (item) => {
    delete item.notes_count;
    _.each(additionalData, (additionalResponse, responseKey) => {
      const action = extraActionsKeys[responseKey];
      let idKey = 'groupId';
      let itemId = item.groupId;
      switch (action) {
        case '_tags':
          idKey = 'id';
          break;
        case '_follow':
          idKey = 'propertyId';
          itemId = item.folio;
          additionalResponse = {
            data: {items: additionalResponse?.data?.entityQuery?.items || []},
          };
          break;
        case 'notes':
          idKey = 'property_id';
          itemId = item.folio;
          break;
        default:
        // Empty.
      }
      const additionalItemRaw = _.find(additionalResponse.data.items, {[idKey]: itemId});
      const additionalItem = additionalItemRaw ? _.cloneDeep(additionalItemRaw) : null;
      if (additionalItem) {
        // Add Flag ID to item for follows.
        if (action === '_follow') {
          additionalItem.followId = additionalItem.id;
        }
        extendWithAdditionalData(item, additionalItem, extraActions[action]);
      }
    });
  });
}

export function* updateListResults(setLoading = true) {
  if (setLoading) {
    yield put(actions.setLoading(true));
  }
  const mapData = yield select(state => state.map.mapData);
  if (!mapData.data_source) {
    return;
  }
  const mapFormData = yield select(state => state.map.mapFormData);
  const mapSearchResults = yield select(state => state.map.mapSearchResults);
  const userData = yield select(state => state.user.userData);
  const subscriptionSettings = yield select(state => state.user.subscriptionSettings);
  const {listParams, formData, filters} = mapFormData;
  const filterParams = prepareFormDataParams(formData, true);
  const placeId = yield select(state => state.map.navigation.place?.placeId);
  const hasParams = !_.isEmpty(filterParams) || mapSearchResults.searchInited || !!placeId;

  // Add selected filters to each request url.
  let resultsUrl = '';
  let stateEnv = utilities.getCurrentStateEnv();
  const enabledFields = {};
  _.each(filters.fields, (field) => {
    if (field.enabled_default) {
      enabledFields[field.name] = 1;
    }
  });
  const {mainActionFields, extraActions, subQueries} = prepareActionsFields(filters.fields, enabledFields);
  const apiPublicToken = yield apiToken();
  // Filter fields by access.
  const mainFieldsWithAccess = ['folio', 'groupId', 'yearBuilt', 'sumCalculatedLotSize', 'primary_owners'];
  _.each(mainActionFields, (field) => {
    if (hasAdditionalFieldAccess(userData, subscriptionSettings, `${filters.action}|${field}`)) {
      mainFieldsWithAccess.push(field);
    }
  });
  const resultsLimit = propertyResultsLimit(userData, subscriptionSettings);
  const offset = listParams.rows * listParams.currentPage;
  let rowsCount = listParams.rows;
  // Check if we should limit our rows output per subscription access.
  let resultsLimited = false;
  if (resultsLimit !== -1) {
    if (resultsLimit > offset && resultsLimit < offset + listParams.rows) {
      rowsCount = offset + listParams.rows - resultsLimit;
      resultsLimited = resultsLimit;
    }
    else if (resultsLimit < offset + listParams.rows) {
      rowsCount = 0;
      resultsLimited = resultsLimit;
    }
  }
  if (hasParams && rowsCount) {
    const searchParams = {
      rows: rowsCount,
      offset: listParams.rows * listParams.currentPage,
      type: filters.action,
      action: filters.action,
      featureLayer: mapData.data_source,
      publicToken: apiPublicToken,
      order_by: listParams.sortField,
      sort_order: listParams.sort,
      'fields[]': mainFieldsWithAccess,
      group_by: 'condoGroup',
      'subquery[0][action]': '_property_record',
      'subquery[0][fields][]': ['id'],
      'subquery[0][from_field]': 'groupId',
      'subquery[0][to_field]': 'groupId',
      'subquery[0][rows]': 2,
      state_env: stateEnv,
      geometryFormat: 'geojson',
      boostQuery: RESULTS_TAB_BOOST_QUERY,
    };
    if (placeId) {
      searchParams.place = placeId;
    }
    const subqueries = [];
    let subQueryIndex = 1;
    _.each(subQueries, (actionData, action) => {
      subqueries.push({
        [`subquery[${subQueryIndex}][action]`]: action,
        [`subquery[${subQueryIndex}][fields][]`]: Object.keys(actionData.fields),
        [`subquery[${subQueryIndex}][from_field]`]: action === '_tags' ? 'id' : 'groupId',
        [`subquery[${subQueryIndex}][to_field]`]: 'groupId',
        [`subquery[${subQueryIndex}][rows]`]: 1,
      });
      subQueryIndex += 1;
    });
    if (subqueries.length) {
      _.assign(searchParams, ...subqueries);
    }
    resultsUrl = new UriJs(`${API_URL}/api/ui-api`)
      .search(searchParams)
      .addSearch(filterParams)
      .normalizeSearch()
      .readable();
  }
  try {
    let results = {};
    if (hasParams && rowsCount) {
      results = _.cloneDeep((yield call(api, {url: resultsUrl, method: 'get'})).data);
      // Get data from extra action requests if they exists.
      const extraRequests = [];
      // Extract ids from items response.
      const ids = _.map(results.items, item => item.folio).filter(Boolean);
      const groupIds = _.map(results.items, item => item.groupId);
      if (groupIds.length) {
        _.each(extraActions, (actionData, action) => {
          if (action === '_zonar') {
            stateEnv = results.items?.[0]?.state?.[0]?.toLowerCase();
          }
          const idFilter = action === '_tags' ? 'id' : 'groupId';
          const extraRequestUrl = new UriJs(`${API_URL}/api/ui-api`)
            .search({
              rows: 100,
              offset: 0,
              type: action,
              action,
              featureLayer: mapData.data_source,
              publicToken: apiPublicToken,
              'fields[]': _.values(actionData.fields).concat(['id', 'groupId']),
              ..._.get(actionData, 'filters', {}),
              [idFilter]: groupIds.join(),
              state_env: stateEnv,
            })
            .normalizeSearch()
            .readable();
          extraRequests.push(
            call(api, {
              url: extraRequestUrl,
              method: 'get',
            }),
          );
        });

        // Proceed data fetching for follows and notes.
        yield additionalRequests(ids, results.items, extraActions, extraRequests, apiPublicToken);
      }
      // Extend item results with subquery values.
      if (!_.isEmpty(subQueries)) {
        _.each(subQueries, (actionData, action) => {
          _.each(_.get(results, 'items', []), (item) => {
            const additionalItem = _.get(item, [action, 0], null);
            if (additionalItem) {
              extendWithAdditionalData(item, additionalItem, actionData);
            }
          });
        });
      }
    }
    // Add rows count if results was limited.
    else if (resultsLimited) {
      results.rows = _.get(mapSearchResults, 'results.rows', 0);
    }
    const data = {results, resultsLimited};
    const resultItems = _.get(results, 'items', []);
    const shouldCalculateBounds = resultItems.length
      && results.geometryFormat === 'geojson'
      && (filterParams.address || filterParams.id_by_address || filterParams.folio_by_id);

    if (shouldCalculateBounds) {
      const overlayData = _.mapValues(resultItems, item => _.get(item, 'gisData.geom'));
      const {bounds} = prepareOverlayGeometry(overlayData, true);
      data.bounds = bounds;
    }
    else {
      data.bounds = null;
    }
    yield put(actions.updateMapSearchResults(data));
    if (setLoading) {
      yield put(actions.setLoading(false));
    }
  }
  catch (e) {
    const yieldActions = [put(actions.updateMapSearchResults({results: {}, resultsLimited}))];
    if (setLoading) {
      yieldActions.push(put(actions.setLoading(false)));
    }
    yield all(yieldActions);
  }
}

export function* fetchFollowedProperties() {
  yield put(actions.setLoading(true));
  const followedProperties = yield select(state => state.map.followedProperties);
  const mapFormData = yield select(state => state.map.mapFormData);
  const mapData = yield select(state => state.map.mapData);
  const userData = yield select(state => state.user.userData);
  const {filters, enabledFields} = mapFormData;
  const {listParams} = followedProperties;
  const {mainActionFields, extraActions} = prepareActionsFields(filters.fields, enabledFields);
  const followsRequest = yield SavedPropertiesRequest(['id', 'propertyId', 'propertyPath'], [], {
    limit: listParams.rows,
    offset: listParams.rows * listParams.currentPage,
  });
  const follows = (yield followsRequest)?.data?.entityQuery;
  if (!follows?.total) {
    yield all([put(actions.setFollowedProperties({results: {}})), put(actions.setLoading(false))]);
    return;
  }
  const followsItems = follows?.items;
  const propertyIds = _.filter(followsItems.map(v => v.propertyId));
  const requestFields = mainActionFields.concat([
    'groupId',
    'yearBuilt',
    'sumCalculatedLotSize',
    'primary_owners',
    'folio',
  ]);

  try {
    let results = {};
    const requestConfigs = [];
    if (propertyIds.length) {
      requestConfigs.push({
        url: utilities.prepareApiUrl('_property_record', '_property_record', {
          publicToken: encodeURIComponent(userData.gridicsApiPublicToken),
          featureLayer: mapData.data_source,
          'fields[]': requestFields,
          rows: listParams.rows,
          folio: propertyIds.join(','),
          offset: 0,
          group_by: 'condoGroup',
        }),
        method: 'get',
      });
    }
    const mainResponses = yield all(requestConfigs.map(config => call(axios, config)));
    const mergedItems = _.cloneDeep(_.uniqBy(
      _.flatMap(mainResponses, response => _.get(response, 'data.items', [])),
      item => item.id,
    ));
    results = {
      ...(mainResponses[0]?.data || {}),
      items: mergedItems,
    };
    // Get data from extra action requests if they exists.
    const extraRequests = [];
    // Extract ids from items response.
    const groupIds = _.map(results.items, item => item.groupId);
    const ids = _.map(results.items, item => item.folio).filter(Boolean);
    if (groupIds.length) {
      _.each(extraActions, (actionData, action) => {
        const idFilter = action === '_tags' ? 'id' : 'groupId';
        const extraRequestUrl = utilities.prepareApiUrl(action, action, {
          rows: 100,
          offset: 0,
          publicToken: encodeURIComponent(userData.gridicsApiPublicToken),
          app: 'sear',
          [idFilter]: groupIds.join(),
          ..._.get(actionData, 'filters', {}),
          'fields[]': _.values(actionData.fields).concat(['groupId', 'id']),
        });
        extraRequests.push(
          call(axios, {
            url: extraRequestUrl,
            method: 'get',
          }),
        );
      });

      // Add notes status request.
      yield addNotesRequest(extraActions, extraRequests, ids);

      // Request additional data.
      const additionalData = yield all(extraRequests);
      // Prepare additional data merging with main items.
      const extraActionsKeys = Object.keys(extraActions);
      _.each(results.items, (item) => {
        item.follows = _.find(followsItems, follow => `${follow.propertyId}` === `${item.folio}`);
        item.followId = item.follows?.id || 0;
        _.each(additionalData, (additionalResponse, responseKey) => {
          const action = extraActionsKeys[responseKey];
          const itemId = action === 'notes' ? item.folio : item.groupId;
          let idKey = 'groupId';
          switch (action) {
            case '_tags':
              idKey = 'id';
              break;
            case 'notes':
              idKey = 'property_id';
              break;
            default:
            // Empty.
          }
          const additionalItem = _.find(additionalResponse.data.items, {[idKey]: itemId});
          if (additionalItem) {
            extendWithAdditionalData(item, additionalItem, extraActions[action]);
          }
        });
      });
    }
    results.rows = follows.total;
    yield put(actions.setFollowedProperties({results, lastFetch: Date.now()}));
    yield put(actions.setLoading(false));
  }
  catch (e) {
    yield all([put(actions.setFollowedProperties({results: {}})), put(actions.setLoading(false))]);
  }
}

/**
 * Sagas handler for search result submit.
 */
export function* updateFilters(action) {
  try {
    let mapFormData = yield select(state => state.map.mapFormData);
    const prevSearchInited = yield select(state => state.map.mapSearchResults.searchInited);
    const incomingFormData = _.get(action, 'data.formData');
    // submitMapSearch always carries formData on the action, but updateListResults / URL
    // parsing read from Redux. If those ever diverge (ordering, batching, or stale closures),
    // results stay stale until something else re-renders the map (e.g. resize → moveend).
    if (incomingFormData !== undefined && !_.isEqual(incomingFormData, mapFormData.formData)) {
      yield put(actions.setMapFormData({formData: incomingFormData}));
      mapFormData = yield select(state => state.map.mapFormData);
    }
    const placeData = yield select(state => state.map.navigation.place);
    // On the first search for a place/filter set, honour a deep-linked results
    // page (?resultsPage=N) so the initial fetch lands on that page directly,
    // instead of loading page one and then jumping (which caused a visible flash).
    if (!prevSearchInited) {
      const rawResultsPage = parseInt(getQueryParam('resultsPage'), 10);
      const urlResultsPage = Number.isFinite(rawResultsPage) && rawResultsPage > 1 ? rawResultsPage - 1 : 0;
      if (urlResultsPage > 0 && mapFormData.listParams.currentPage !== urlResultsPage) {
        yield put(actions.setMapFormData({listParams: {...mapFormData.listParams, currentPage: urlResultsPage}}));
        mapFormData = yield select(state => state.map.mapFormData);
      }
    }
    const {formData} = mapFormData;
    const hasFilters = !_.isEmpty(formData) || placeData?.placeId;
    // Record what we actually submit here, at the single submit choke point, so the filter
    // form's "nothing changed" dedup (MapFilters.save) stays correct across every path —
    // applying a filter, removing one from ActiveFilters, or Clear All. A component-local
    // snapshot went stale when filters changed outside MapFilters (re-applying a removed
    // filter became a no-op).
    yield put(actions.setMapFormData({lastSubmittedFormData: formData}));
    yield put(actions.setLoading(true));
    yield put(
      actions.updateMapSearchResults({
        searchInited: !!hasFilters,
        clutersLoading: true,
      }),
    );
    if (action.updateParam) {
      // If some early init flow dispatches submitMapSearch with empty formData,
      // do not wipe existing filter params from the URL on refresh.
      // Clearing is handled by explicit reset actions (resetMapSearchResults / Clear button).
      const currentSearch = typeof window !== 'undefined' ? window.location?.search || '' : '';
      const hasUrlFilters = urlHasRestorableFilterParams(currentSearch, mapFormData.filters);
      // Only skip on initial load (search wasn't previously inited). If the user clears filters,
      // we must allow URL to be rewritten to remove filter params.
      if (_.isEmpty(formData) && !prevSearchInited && hasUrlFilters !== false) {
        // Skip URL rewrite; still run the search/facets updates below.
      }
      else {
        const parser = new FiltersParser();
        const [, urlParams, exclude] = parser.parseToUrl(formData, mapFormData.filters).getResult();
        const paramArr = [];
        Object.entries(urlParams).forEach(([name, value]) => {
          paramArr.push({name, value});
        });
        if (!_.isEmpty(mapFormData.filters)) {
          exclude.forEach((name) => {
            paramArr.push({name: `${name}-min`, value: ''});
            paramArr.push({name: `${name}-max`, value: ''});
            paramArr.push({name, value: ''});
          });
        }
        setQueryParamArray(paramArr, 2);
      }
    }
    // Prepare ajax calls.
    yield all([call(updateListResults, false), call(updateFacets, action, false)]);
    yield put(actions.setLoading(false));
    // Only show clusters when filters/place are applied.
    if (hasFilters) {
      yield call(updateClusters, {}, true);
    }
  }
  catch (e) {
    console.error(e);
    yield put(actions.updateMapSearchResults({}));
    yield put(actions.setLoading(false));
  }
}

export function* getTransitTypes() {
  const mapData = yield select(state => state.map.mapData);
  const stateEnv = utilities.getCurrentStateEnv();
  const apiPublicToken = yield apiToken();

  const facetsUrl = new UriJs(`${API_URL}/api/ui-api`).search({
    rows: 100,
    offset: 0,
    type: 'facets_info',
    action: '_transit_route',
    featureLayer: mapData.data_source,
    publicToken: apiPublicToken,
    'fields[]': 'title',
    allFacets: '1',
    state_env: stateEnv,
  });

  const placeId = yield select(state => state.map.navigation.place?.placeId);
  if (placeId) {
    facetsUrl.addSearch('place', placeId);
  }

  try {
    const results = yield call(axios, facetsUrl.normalizeSearch().readable());
    yield put(
      actions.updateMapSearchResults({
        vehicleTypes: _.get(results, 'data.facets.vehicleType'),
      }),
    );
  }
  catch (e) {
    yield all([put(actions.updateMapSearchResults({vehicleTypess: {}}))]);
  }
}

export function* getPoiCategories(action) {
  const mapData = yield select(state => state.map.mapData);
  const stateEnv = utilities.getCurrentStateEnv();
  const apiPublicToken = yield apiToken();
  const facetsUrl = new UriJs(`${API_URL}/api/ui-api`).search({
    rows: 100,
    offset: 0,
    featureLayer: mapData.data_source,
    type: 'facets_info',
    action: '_points_of_interest',
    publicToken: apiPublicToken,
    'fields[]': 'title',
    state_env: stateEnv,
  });

  const placeId = yield select(state => state.map.navigation.place?.placeId);
  if (placeId) {
    facetsUrl.addSearch('place', placeId);
  }

  _.each(action.filters, (value, name) => {
    if (value !== '_') {
      facetsUrl.addSearch(name, value);
    }
  });

  try {
    const results = yield call(api, {
      url: facetsUrl.normalizeSearch().readable(),
      method: 'get',
    });
    const resultsCount = parseInt(_.get(results, 'data.count').replace(/,/g, ''), 10);
    yield put(
      actions.updateMapSearchResults({
        poiCategories: {
          primaryCategory: _.get(results, 'data.facets.primaryCategory'),
          secondaryCategory: _.get(results, 'data.facets.secondaryCategory'),
          tertiaryCategory: _.get(results, 'data.facets.tertiaryCategory'),
        },
        poiResultsCount: resultsCount,
      }),
    );
  }
  catch (e) {
    yield all([
      put(
        actions.updateMapSearchResults({
          poiCategories: {},
          poiResultsCount: 0,
        }),
      ),
    ]);
  }
}

/**
 * Fetch additional data for recent properties.
 *
 * @param {*} action
 */
export function* fetchRecentAdditions(action) {
  const {setLoading} = action;
  const items = _.cloneDeep(action.items || []);
  // Add selected filters to each request url.
  const apiPublicToken = yield apiToken();

  try {
    // Get data from extra action requests if they exists.
    const extraRequests = [];
    // Extract ids from items response and remove fields that should be actualized.
    const ids = [];
    _.each(items, (item) => {
      ids.push(item.id);
      _.unset(item, 'notes_count');
      _.unset(item, 'followId');
    });
    if (ids.length) {
      if (setLoading) {
        yield put(actions.setLoading(true));
      }
      // Proceed data fetching for follows and notes.
      yield additionalRequests(ids, items, {}, extraRequests, apiPublicToken);
      // Put back recent properties in correct object format where the key is a folio.
      const preparedItems = _.mapKeys(items, item => item.folio);
      yield put(
        actions.setRecentProperties(prevPropsData => ({
          results: {
            ...prevPropsData.results,
            ...preparedItems,
          },
        })),
      );
      if (setLoading) {
        yield put(actions.setLoading(false));
      }
    }
  }
  catch (e) {
    if (setLoading) {
      yield put(actions.setLoading(false));
    }
  }
}

export function* getJurisdictions(action) {
  const stateEnv = utilities.getCurrentStateEnv();
  const apiPublicToken = yield apiToken();
  const {filter} = action.data;
  const filterToApply = _.get(filter, 'settings.filters._land_use', {});
  const facetsUrl = new UriJs(`${API_URL}/api/ui-api`).search({
    rows: 100,
    offset: 0,
    type: 'facets_info',
    action: '_land_use',
    publicToken: apiPublicToken,
    'fields[]': 'jurisdiction',
    [filterToApply.field]: filterToApply.value,
    state_env: stateEnv,
  });

  const placeId = yield select(state => state.map.navigation.place?.placeId);
  if (placeId) {
    facetsUrl.addSearch('place', placeId);
  }

  try {
    const results = yield call(api, {
      url: facetsUrl.normalizeSearch().readable(),
      method: 'get',
    });
    const jurisdictions = _.get(results, 'data.facets.jurisdiction');
    yield put(
      actions.updateMapSearchResults(prevDataResults => ({
        jurisdictions: {
          ...prevDataResults.jurisdictions,
          ...{[filter.name]: jurisdictions.sort((a, b) => (a.title > b.title ? 1 : -1))},
        },
      })),
    );
  }
  catch (e) {
    yield all([
      put(
        actions.updateMapSearchResults({
          jurisdictions: {},
        }),
      ),
    ]);
  }
}

export function* initClusters() {
  yield call(updateClusters, {}, true);
}
